import { Box, Typography, useTheme } from '@mui/material';
import DatePicker from 'components/Common/DatePicker';
import { useForm } from 'react-hook-form';
import Grid from '@mui/material/Grid2';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer
} from 'recharts';

import dayjs from 'dayjs';
import Spinner from 'components/Common/Spinner';
import { useGetPerformance } from 'apis/business/agent/performance/hook';
import { formatDuration } from 'utils/helperFunctions';
import { MdOutlineAccessTime, MdOutlineTextsms } from 'react-icons/md';
import { IoMdStopwatch } from 'react-icons/io';
import { BiPhoneCall } from 'react-icons/bi';

export default function Performance() {
  const theme = useTheme();
  const { control, watch } = useForm();

  const filters = watch();

  const { data: performance, isLoading } = useGetPerformance({
    ...filters
  });

  const COLORS = [theme.palette.primary.light, theme.palette.primary.main, theme.palette.primary.dark];

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <>
      <Box display='flex' alignItems='center' justifyContent='space-between' mb={3}>
        <Box display={{ xs: 'block', md: 'flex' }} alignItems='center' gap={2}>
          <Typography variant='h5' fontWeight='500'>
            Performance
          </Typography>
        </Box>
        <DatePicker name='date' control={control} defaultValue={dayjs()} size='small' />
      </Box>
      <Grid container spacing={3} mb={3}>
        <Grid size={3} bgcolor='#fff' border='1px solid #ECECEC' paddingY={3} paddingX={2} borderRadius='4px'>
          <Box display='flex' gap='4px' alignItems='center'>
            <Box
              bgcolor={theme.palette.primary.light}
              borderRadius='50%'
              height='32px'
              width='32px'
              display='flex'
              alignItems='center'
              justifyContent='center'
            >
              <MdOutlineTextsms color={theme.palette.primary.main} fontSize={20} />
            </Box>
            <Typography color='secondary' fontWeight={500}>
              Call Counts
            </Typography>
          </Box>
          <Typography variant='h5' mt={5} fontWeight={500}>
            {performance?.overview?.call_counts}
          </Typography>
        </Grid>
        <Grid size={3} bgcolor='#fff' border='1px solid #ECECEC' paddingY={3} paddingX={2} borderRadius='4px'>
          <Box display='flex' gap='4px' alignItems='center'>
            <Box
              bgcolor={theme.palette.primary.light}
              borderRadius='50%'
              height='32px'
              width='32px'
              display='flex'
              alignItems='center'
              justifyContent='center'
            >
              <MdOutlineAccessTime color={theme.palette.primary.main} fontSize={20} />
            </Box>
            <Typography color='secondary' fontWeight={500}>
              Call Duration
            </Typography>
          </Box>
          <Typography variant='h5' mt={5} fontWeight={500}>
            {formatDuration(performance?.overview?.calls_duration)}
          </Typography>
        </Grid>
        <Grid size={3} bgcolor='#fff' border='1px solid #ECECEC' paddingY={3} paddingX={2} borderRadius='4px'>
          <Box display='flex' gap='4px' alignItems='center'>
            <Box
              bgcolor={theme.palette.primary.light}
              borderRadius='50%'
              height='32px'
              width='32px'
              display='flex'
              alignItems='center'
              justifyContent='center'
            >
              <IoMdStopwatch color={theme.palette.primary.main} fontSize={20} />
            </Box>
            <Typography color='secondary' fontWeight={500}>
              Average Duration
            </Typography>
          </Box>
          <Typography variant='h5' mt={5} fontWeight={500}>
            {formatDuration(performance?.overview?.average_duration)}
          </Typography>
        </Grid>
        <Grid size={3} bgcolor='#fff' border='1px solid #ECECEC' paddingY={3} paddingX={2} borderRadius='4px'>
          <Box display='flex' gap='4px' alignItems='start'>
            <Box
              bgcolor={theme.palette.primary.light}
              borderRadius='50%'
              height='32px'
              width='32px'
              display='flex'
              alignItems='center'
              justifyContent='center'
            >
              <BiPhoneCall color={theme.palette.primary.main} fontSize={20} />
            </Box>
            <Typography color='secondary' fontWeight={500}>
              New Unique Call
            </Typography>
          </Box>
          <Typography variant='h5' mt={5} fontWeight={500}>
            {performance?.overview?.unique_calls}
          </Typography>
        </Grid>
      </Grid>
      <Box bgcolor={theme.palette.background.paper} padding={3}>
        <Typography variant='h6' fontWeight='500'>
          Daily Call Performance
        </Typography>
        <Grid container>
          <Grid size={12}>
            <Box sx={{ width: '100%', minWidth: 0 }}>
              <ResponsiveContainer width='100%' height={400}>
                <BarChart
                  key={JSON.stringify(performance?.daily_performance?.call_performance)}
                  data={performance?.daily_performance?.call_performance}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 20
                  }}
                >
                  <CartesianGrid strokeDasharray='3 3' vertical={false} />
                  <XAxis dataKey='time' />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey='records_count' name='Record Count' fill={theme.palette.primary.main} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Grid container spacing={3} mt={3}>
        <Grid size={{ xs: 12, lg: 6 }} bgcolor={theme.palette.background.paper} padding={3}>
          <Typography variant='h6' fontWeight='500'>
            Customer Breakdown
          </Typography>
          <Box sx={{ width: '100%', minWidth: 0 }}>
            <ResponsiveContainer width='100%' height={300}>
              <PieChart>
                <Pie
                  data={performance?.customer_breakdown}
                  innerRadius={70}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey='value'
                  startAngle={90}
                  endAngle={450}
                >
                  {performance?.customer_breakdown?.map((item: any, index: number) => (
                    <Cell
                      key={`cell-${index}`}
                      name={item?.name.replace(/_/g, ' ')}
                      fill={[theme.palette.primary.main, theme.palette.primary.light][index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend layout='vertical' align='right' verticalAlign='middle' />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, lg: 6 }} bgcolor={theme.palette.background.paper} padding={3}>
          <Typography variant='h6' fontWeight='500'>
            Action Breakdown
          </Typography>
          <Box sx={{ width: '100%', minWidth: 0 }}>
            <ResponsiveContainer width='100%' height={300}>
              <PieChart>
                <Pie
                  data={performance?.action_breakdown}
                  innerRadius={70}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey='value'
                  startAngle={90}
                  endAngle={450}
                >
                  {performance?.action_breakdown?.map((item: any, index: number) => (
                    <Cell
                      key={`cell-${index}`}
                      name={item?.name.replace(/_/g, ' ')}
                      fill={
                        [theme.palette.primary.light, theme.palette.primary.main, theme.palette.primary.dark][
                          index % COLORS.length
                        ]
                      }
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend layout='vertical' align='right' verticalAlign='middle' />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Grid>
      </Grid>
    </>
  );
}
